import { union } from 'es-toolkit';

import { htmlToText } from '@/utils/htmlToText';

export const getSource = (url: string) => {
  const urlObj = new URL(url);
  const host = urlObj.hostname;
  if (host.includes('youtube')) {
    return 'youtube';
  }
  return 'podcast';
};

export const YOUTUBE_THUMBNAIL_WIDTHS = {
  mqdefault: 320,
  hqdefault: 480,
  sddefault: 640,
  maxresdefault: 1280,
} as const;

export type YouTubeThumbnailSize = keyof typeof YOUTUBE_THUMBNAIL_WIDTHS;

const YOUTUBE_THUMBNAIL_SIZES = Object.keys(YOUTUBE_THUMBNAIL_WIDTHS) as YouTubeThumbnailSize[];
const DEFAULT_YOUTUBE_THUMBNAIL_SIZE: YouTubeThumbnailSize = 'hqdefault';
const ALWAYS_AVAILABLE_YOUTUBE_THUMBNAIL_SIZES: YouTubeThumbnailSize[] = ['mqdefault', 'hqdefault'];

export const getYouTubeThumbnailBaseUrl = (thumbnailUrl: string | undefined): string => {
  if (!thumbnailUrl) return '';
  return thumbnailUrl.split('/').slice(0, -1).join('/');
};

export const getYouTubeThumbnailSize = (
  thumbnailUrl: string | undefined,
  thumbnailWidth?: number,
): YouTubeThumbnailSize => {
  const filename = thumbnailUrl
    ?.split('/')
    .pop()
    ?.split('?')[0]
    ?.replace(/\.[^.]+$/, '');
  if (filename && filename in YOUTUBE_THUMBNAIL_WIDTHS) {
    return filename as YouTubeThumbnailSize;
  }

  if (typeof thumbnailWidth === 'number' && Number.isFinite(thumbnailWidth) && thumbnailWidth > 0) {
    return (
      YOUTUBE_THUMBNAIL_SIZES.findLast((size) => YOUTUBE_THUMBNAIL_WIDTHS[size] <= thumbnailWidth) ??
      ALWAYS_AVAILABLE_YOUTUBE_THUMBNAIL_SIZES[0]
    );
  }

  return DEFAULT_YOUTUBE_THUMBNAIL_SIZE;
};

export const buildYouTubeThumbnailUrl = (baseUrl: string, size: YouTubeThumbnailSize): string => {
  return `${baseUrl}/${size}.jpg`;
};

export const buildYouTubeThumbnailSrcSet = (
  baseUrl: string,
  maxSize: YouTubeThumbnailSize = DEFAULT_YOUTUBE_THUMBNAIL_SIZE,
): string => {
  return union(ALWAYS_AVAILABLE_YOUTUBE_THUMBNAIL_SIZES, [maxSize])
    .map((size) => `${buildYouTubeThumbnailUrl(baseUrl, size)} ${YOUTUBE_THUMBNAIL_WIDTHS[size]}w`)
    .join(', ');
};

export const buildYouTubeThumbnailSizes = (columns?: string): string => {
  const parsed = Number.parseInt(columns || '3', 10);
  const columnCount = Number.isFinite(parsed) && parsed > 0 ? parsed : 3;
  return `(max-width: 640px) 100vw, ${Math.round(100 / columnCount)}vw`;
};

export const formatDate = (date: string) => {
  const dateTime = new Date(date);
  return dateTime.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const getEpisodeSlug = (title: string) => {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug;
};

export const getEpisodeUrl = (title: string, feedId: string) => {
  const slug = getEpisodeSlug(title);
  return `/podcast/${slug}/${feedId}`;
};

export const stripHtmlTags = (html: unknown): string => (typeof html === 'string' ? htmlToText(html) : '');

export const normalizeToArray = <T>(value: T | T[] | undefined | null): T[] => {
  if (Array.isArray(value)) return value;
  if (value != null) return [value];
  return [];
};
